/**
 * `import-progress` — the assistant's reusable "a batch import is running" artifact.
 *
 * Payload is two identifiers and nothing else: `{ batchProcessId, channelLabel? }`.
 * Everything shown is read live, so the same block is correct whether you open the
 * conversation now or a week from now.
 *
 * ─── Why it reads from two places ───────────────────────────────────────────
 * `batchProcesses/shared` carries an always-on subscription over the ACTIVE
 * processes, which is what makes the counters tick without this block opening a
 * socket of its own. But that store deliberately DROPS a process once it
 * converges (`shouldAutoRemoveProcess`), and a transcript must not go blank
 * when the thing it describes finishes. So:
 *
 *   • present in the shared store  → render it live, and keep a snapshot of the
 *     last thing seen so the terminal state survives the removal;
 *   • absent and never seen        → fetch it once by id (`BatchProcessesById`),
 *     which is the reload case: the import finished days ago and the shared
 *     store legitimately knows nothing about it.
 *
 * ─── Status is DERIVED, never read raw ──────────────────────────────────────
 * The per-item progress events project before `BatchProcessCompleted` does, so
 * there is a window where the readmodel still says `running` with the counters
 * already full. `effectiveStatus` is the one derivation of what to display; it
 * is imported, not re-implemented, because two copies of that rule are exactly
 * how two views end up disagreeing about whether an import is still going.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import type { BatchProcessesReadmodelType } from '@bett3r-dev/teselly-domain';
import {
  useBatchProcessesSharedSelector
} from '@domains/batchProcesses/shared/state/store';
import { effectiveStatus } from '@domains/batchProcesses/list/components/BatchProcessesList/effectiveStatus';
import type { MessageBlockRendererProps } from '@domains/aiAssistant/state/messageBlocks';
import useBackend, { BackendContextType } from '@contexts/useBackend/useBackend';
import { componentsTestIds } from '@components/testids';
import classes from './ImportProgressBlock.module.css';

/**
 * Which import this block is following.
 *
 * A CHANNEL connection can produce two independent batch processes — the
 * publications import and the historical order seeding — and they run, finish and
 * fail separately. Both are the same artifact: a denominator, a counter, a
 * progress bar, a derived status. Only three strings differ.
 *
 * So this is a payload field, NOT a second component. The block's whole reason to
 * exist is that the wizard and the assistant read ONE implementation and therefore
 * cannot disagree about progress; a forked `OrderImportProgressBlock` would
 * reintroduce exactly the divergence this file was written to prevent, and would
 * have to be kept in step with every future change to `effectiveStatus`, the
 * two-source read, and the terminal-state snapshot.
 *
 * Absent means `publications` — every message persisted before the order seeding
 * existed carries no `kind`, and a transcript is read back long after it is written.
 */
export type ImportProgressKind = 'publications' | 'orders';

/** The three strings that are not kind-neutral. Everything else — "Preparando la
 *  importación…", "Importación terminada", "CON ERROR", "PROGRESO" — reads
 *  correctly for both and is looked up directly. */
const KIND_COPY: Record<ImportProgressKind, {
  running: [ string, string ];
  runningNamed: [ string, string ];
  ok: [ string, string ];
}> = {
  publications: {
    running: [ 'ai_import_progress_running', 'Importando publicaciones' ],
    runningNamed: [ 'ai_import_progress_running_named', 'Importando publicaciones de {{channel}}' ],
    ok: [ 'ai_import_progress_ok', 'IMPORTADAS' ]
  },
  orders: {
    running: [ 'ai_import_orders_progress_running', 'Importando pedidos' ],
    runningNamed: [ 'ai_import_orders_progress_running_named', 'Importando el historial de pedidos de {{channel}}' ],
    ok: [ 'ai_import_orders_progress_ok', 'IMPORTADOS' ]
  }
};

export type ImportProgressPayload = {
  /** Batch process to follow. Required — without it the block has nothing to show. */
  batchProcessId?: string;
  /** Channel name, for copy. Purely cosmetic; the block works without it. */
  channelLabel?: string;
  /** Which import. Defaults to `publications` — see `ImportProgressKind`. */
  kind?: ImportProgressKind;
};

export const ImportProgressBlock = ({ block }: MessageBlockRendererProps ) => {
  const { t } = useTranslation( 'interactions' );
  const { backend } = useBackend() as BackendContextType;
  const payload = ( block.payload ?? {}) as ImportProgressPayload;
  const { batchProcessId, channelLabel } = payload;
  // An unknown `kind` from a future build degrades to the publications copy
  // rather than to a card with no title at all.
  const kind: ImportProgressKind = payload.kind === 'orders' ? 'orders' : 'publications';
  const copy = KIND_COPY[kind];

  const live = useBatchProcessesSharedSelector(( s ) =>
    batchProcessId
      ? s.activeProcesses.find(( p ) => p.id === batchProcessId )
      : undefined );

  /**
   * Last thing we saw, from either source. Holding it is what lets the block
   * keep showing "importé N publicaciones" after the shared store drops the
   * converged process — the alternative is a card that empties itself at the
   * exact moment it has good news.
   */
  const [ seen, setSeen ] = useState<BatchProcessesReadmodelType | null>( null );

  useEffect(() => {
    if ( live ) setSeen( live as BatchProcessesReadmodelType );
  }, [live]);

  // Reload case: nothing live and nothing seen this session → ask once.
  useEffect(() => {
    if ( !batchProcessId || live || seen ) return;
    let cancelled = false;
    backend.queries
      .BatchProcessesById( batchProcessId )
      .then(( result ) => {
        // Guard the late resolution: without it a fetch that settles after the
        // subscription delivered a fresher row would overwrite it with older data.
        if ( !cancelled && result ) setSeen( result as BatchProcessesReadmodelType );
      })
      .catch(() => {
        // Nothing to recover — the block falls through to its unknown state,
        // and the global error handler is not the right surface for a widget
        // that is decoration on a message.
      });
    return () => { cancelled = true; };
  }, [ batchProcessId, live, seen, backend ]);

  const process = ( live as BatchProcessesReadmodelType | undefined ) ?? seen;

  const status = useMemo(
    () => ( process ? effectiveStatus( process ) : null ),
    [process]
  );

  if ( !batchProcessId ) return null;

  if ( !process ) {
    return (
      <div className={classes.block}>
        <div className={classes.head}>
          <span className={classes.spinner} />
          <span className={classes.title}>
            {t( 'ai_import_progress_starting', 'Preparando la importación…' )}
          </span>
        </div>
      </div>
    );
  }

  /**
   * Derived from the DECLARED readmodel fields, not from `successCount` /
   * `errorCount`. Those two are written by the projector through an `as any`
   * upsert and are absent from `BatchProcessesReadmodelType`, so reading them
   * would be trusting a field the type says does not exist.
   *
   * `completedTargetIds` holds every target that was PROCESSED — successes and
   * failures alike — which is why `resetForRetry` gets the successes by
   * filtering the errored ones out of it. Same subtraction here.
   */
  const total = process.totalItems ?? 0;
  const done = process.completedCount ?? 0;
  const failed = process.erroredTargetIds?.length ?? 0;
  const ok = Math.max( 0, done - failed );
  // `totalItems` is 0 until the run is sized, and a 0/0 bar reading 100% would
  // announce a finished import before a single item moved.
  const pct = total > 0 ? Math.min( 100, Math.round(( done / total ) * 100 )) : 0;
  const isRunning = status === 'running';
  const isErrored = status === 'errored';

  return (
    <div className={classes.block} data-testid={componentsTestIds.aiImportProgress( kind )}>
      <div className={classes.head}>
        {isRunning && <span className={classes.spinner} />}
        {status === 'completed' && (
          <span className={classes.doneMark}><IconCheck size={12} stroke={3} /></span>
        )}
        {isErrored && (
          <span className={classes.errMark}><IconAlertTriangle size={12} stroke={2.4} /></span>
        )}

        <span className={classes.title}>
          {isRunning
            ? ( channelLabel
              ? t( copy.runningNamed[0], copy.runningNamed[1], { channel: channelLabel })
              : t( copy.running[0], copy.running[1]))
            : isErrored
              ? t( 'ai_import_progress_errored', 'La importación terminó con errores' )
              : t( 'ai_import_progress_done', 'Importación terminada' )}
        </span>

        <span className={classes.count} data-testid={componentsTestIds.aiImportProgressCount( kind )}>
          {total > 0 ? `${done} / ${total}` : done}
        </span>
      </div>

      <div className={classes.track}>
        <span
          className={classes.fill}
          data-state={isErrored ? 'errored' : status}
          style={{ width: `${ total > 0 ? pct : 0 }%` }}
        />
      </div>

      <div className={classes.stats}>
        <div>
          <span className={classes.v}>{ok}</span>
          <span className={classes.k}>{t( copy.ok[0], copy.ok[1])}</span>
        </div>
        <div>
          <span className={classes.v}>{failed}</span>
          <span className={classes.k}>{t( 'ai_import_progress_failed', 'CON ERROR' )}</span>
        </div>
        <div>
          <span className={classes.v}>{total > 0 ? `${pct}%` : '—'}</span>
          <span className={classes.k}>{t( 'ai_import_progress_pct', 'PROGRESO' )}</span>
        </div>
      </div>
    </div>
  );
};

export default ImportProgressBlock;
